import { Match, mergeProps, Show, Switch, useContext } from 'solid-js';
import { ArrowRightIcon } from '../../components/icons-library/arrow-right';
import { StyledFlexRow } from '../utility-style-components/flex';
import { ButtonProps, ButtonWpAttrs } from './button-types';
import { ErrorCatcher } from '../../tools/error-catcher';
import { navigateToAnchor } from '../../tools/scroll-to-element';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import removeAmpersand from '../../tools/remove-ampersand';
import { isExternalLink } from '../../tools/check-is-external-url';
import { Link as RouterLink } from '@solidjs/router';
import { Link } from '../link/link';
import clsx from 'clsx';
import theme from '../../style/theme';

const defaultProps = {
    variant: 'primary',
    darkMode: false,
};

const buttonBase = [
    'rounded-[4.29rem]',
    'my-[0.71rem]',
    'cursor-pointer',
    'border-2',
    'tracking-[0.45px]',
    'font-atos-bold',
    'font-extrabold',
    'uppercase',
    'text-center',
    'w-fit',
    'transition-colors',
    'duration-200',
    'max-mobile:tracking-[0.4px]',
    'max-mobile:leading-[1.93rem]',
];


const alignmentClassMap = {
    left: 'self-start',
    center: 'self-center',
    right: 'self-end',
} as const;

export const Button = (componentProps: ButtonProps) => {
    const { siteInfo, localize } = useContext(AppContext);
    const props = mergeProps(defaultProps, componentProps) as ButtonProps;


    const buttonClass = (as: 'button' | 'link' = 'button') => {
        const variant = props.variant || 'primary';

        return clsx(
            variant !== 'tertiary' && buttonBase, as === 'link' && ['block'],
            props.noBlockSpacing && ['my-0', 'tablet:my-0'],
            props.horizontalAlignment && alignmentClassMap[props.horizontalAlignment],
            props.positionToBottom && 'mt-auto',
            props.customCss,
            props.disabled && [
                'pointer-events-none',
                variant === 'secondary' && ['text-gray-light', 'border-gray-light'],
                variant === 'tertiary' && 'text-gray-light',
                !['secondary', 'tertiary'].includes(variant) && [
                    'bg-gray-light',
                    'text-white',
                ],
            ],
            variant !== 'tertiary' && (
                props.small ? [
                    'px-[1.15rem]',
                    'py-[0.6rem]',
                    'text-[1.14rem]',
                ] : [
                    'px-[clamp(3.36rem,3.36rem+0.35vw,4.29rem)]',
                    'py-[clamp(1.21rem,1.21rem+1.21vw,1.79rem)]',
                    'text-[clamp(1.14rem,1.14rem+1.14vw,1.29rem)]',
                    'max-mobile:px-[3.36rem]',
                    'max-mobile:py-[1.5rem]',
                    'max-mobile:text-[1.4rem]',
                ]
            ),
            !props.disabled &&
                variant === 'primary' &&
                (props.darkMode
                    ? [
                        'bg-transparent',
                        'border-2',
                        'border-white',
                        'text-white',
                        'transition-colors',
                        'duration-200',
                        'hover:bg-white',
                        'hover:text-gray-dark',
                        'hover:border-white',
                        'active:bg-transparent',
                        'active:border-grey-400',
                        'active:text-grey-400',
                    ]
                    : [
                        'bg-primary',
                        'text-white',
                        'border-transparent',
                        'hover:bg-grey-500',
                        'active:bg-grey-400',
                    ]),

            !props.disabled &&
                variant === 'secondary' &&
                (props.darkMode
                    ? [
                        'bg-transparent',
                        'border-2',
                        'border-white',
                        'text-white',
                        'transition-colors',
                        'duration-200',
                        'hover:bg-white',
                        'hover:text-gray-dark',
                        'hover:border-white',
                        'active:bg-transparent',
                        'active:border-grey-400',
                        'active:text-grey-400',
                    ]
                    : [
                        'bg-transparent',
                        'text-primary',
                        'border-primary',
                        'hover:bg-transparent',
                        'hover:text-grey-500',
                        'hover:border-grey-500',
                        'active:bg-transparent',
                        'active:text-grey-400',
                        'active:border-grey-400',
                    ]),

            !props.disabled &&
                variant === 'tertiary' && [
                'block',
                'underline',
                'text-primary',
                'cursor-pointer',
                'border-none',
                'tracking-[0]',
                'font-atos-bold',
                'font-extrabold',
                'uppercase',
                'text-center',
                'w-fit',
                'my-[0.71rem]',
                '!text-[1.43rem]',
                'transition-colors',
                'duration-200',
                'bg-transparent',
                'hover:text-grey-500',
                'active:text-grey-400',
                props.noCaps && 'normal-case',
            ],

            props.darkMode &&
                (variant === 'tertiary'
                    && [
                        'bg-transparent',
                        'border-none',
                        'text-white',
                        'underline',
                        'transition-colors',
                        'duration-200',
                        'hover:text-grey-300',
                        'active:text-grey-400',
                        '!px-0',
                        '!py-0',
                    ]),



            props.isActive &&
                (props.darkMode
                    ? [
                        'bg-transparent',
                        'border-2',
                        'border-pink-light',
                        'text-pink-light',
                    ]
                    : [
                        variant === 'secondary'
                            ? 'border-pink-light text-pink-light bg-transparent'
                            : variant === 'tertiary'
                                ? 'text-pink-light'
                                : 'bg-pink-light'
                    ])
        );
    };


    let url = removeTrailingSlash(props.url);
    url = removeAmpersand(url);

    const anchorButton = () => {
        return (
            <ErrorCatcher componentName='Button'>
                <button
                    class={buttonClass('link')}
                    type={props.type || 'button'}
                    disabled={props.disabled}
                    onClick={(e: any) =>
                        navigateToAnchor({
                            event: e,
                            id: url,
                            isCareSite: siteInfo.siteType === 'atos-care',
                        })
                    }
                    {...(props.label
                        ? {}
                        : { 'aria-label': localize('click', 'Click') })}
                >
                    {props.label
                        ? buttonRemoveAmpersand(props.label)
                        : localize('click', 'Click')}
                </button>
            </ErrorCatcher>
        );
    };

    const buttonRemoveAmpersand = (text?: string) => {
        if (!text) {
            return '';
        }
        /**
         * If you type "&amp;" in the CMS, it will be converted to "&amp;amp;" by the time it gets here.
         * We normally handle removing "&amp;" in removeAmpersand function, but we need to handle "&amp;amp;" here.
         */
        return removeAmpersand(text.replace(/&amp;amp;/g, '&'));
    };

    if (props.variant === 'arrow') {
        const alignmentMap = {
            'left': 'flex-start',
            'center': 'center',
            'right': 'flex-end',
        };

        const style = {
            ...props.positionToBottom ? { 'margin-top': 'auto' } : {},
            ...props.horizontalAlignment ? { 'align-self': alignmentMap[ props.horizontalAlignment ] } : {}
        };

        let target = props.target;
        if (url?.match(/\/(wp-content|download)/i)) {
            target = '_blank';
        }

        return (
            <div style={style}>
                <Link
                    label={props.label || ''}
                    url={url || ''}
                    arrowPosition='right'
                    arrowDirection='right'
                    plainStyle={true}
                    arrowColor={theme.palette.primary}
                    labelColor={theme.palette.primary}
                    bold={true}
                    size='large'
                    element={(el: HTMLElement) => props.element && props.element(el)}
                    opensInNewTab={Boolean(target)}
                    asButton={!url}
                    onClick={props.onClick}
                />
            </div>
        );
    
    } else if (props.isLink) {

        if (url?.slice(0,1) === '#' && !isExternalLink(url) && !url?.match(/\/(wp-content|download)/i)) {
            return anchorButton();
        }

        let target = props.target;
        if (url?.match(/\/(wp-content|download)/i)) {
            target = '_blank';
        }

        return (
            <ErrorCatcher componentName='Button'>
                <Switch>
                    <Match when={target}>
                        <RouterLink
                            class={buttonClass('link')}
                            href={url ? url : '#'}
                            target={target}
                            rel="noopener noreferrer"
                            ref={(el: HTMLElement) => props.element && props.element(el)}
                            style={props.customCss}
                        >
                            {props.label ? buttonRemoveAmpersand(props.label) : 'Click'}
                        </RouterLink>
                    </Match>
                    <Match when={!target}>
                        <RouterLink
                            class={buttonClass('link')}
                            href={url ? url : '#'}
                           
                            ref={(el: HTMLElement) => props.element && props.element(el)}
                            style={props.customCss}
                        >
                            <Show when={props.arrow}>
                                <StyledFlexRow>
                                    <ArrowRightIcon height={1.29}
                                        fill={props.disabled ? theme.palette.lightGray : props.darkMode ? theme.palette.white : theme.palette.primary}
                                    />
                                    {props.label ? buttonRemoveAmpersand(props.label) : 'Click'}
                                </StyledFlexRow>
                            </Show>
                            <Show when={!props.arrow}>
                                {props.label ? buttonRemoveAmpersand(props.label) : 'Click'}
                            </Show>
                        </RouterLink>
                    </Match>
                </Switch>
            </ErrorCatcher>
        );
    } else if (props.arrow && props.variant === 'tertiary') {
        return (
            <ErrorCatcher componentName="Button">
                <button
                    class={buttonClass('link')}
                    type={props.type || 'button'}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    ref={(el: HTMLElement) => props.element && props.element(el)}
                    {...(props.label
                        ? {}
                        : { 'aria-label': localize('click', 'Click') })}
                >
                    <StyledFlexRow>
                        <ArrowRightIcon height={1.29}
                            fill={props.disabled ? 'text-light-gray' : props.darkMode ? theme.palette.white : theme.palette.primary}
                        />
                        <div innerHTML={buttonRemoveAmpersand(props.label)} />
                    </StyledFlexRow>
                </button>
            </ErrorCatcher>
        );
    } else {
        return (
            <ErrorCatcher componentName="Button">
                <button
                    style={props.customCss}
                    id={props.id}
                    class={buttonClass('button')}
                    type={props.type || 'button'}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    ref={(el: HTMLElement) => props.element && props.element(el)}
                    {...(props.label
                        ? {}
                        : { 'aria-label': localize('click', 'Click') })}
                >
                    <div innerHTML={buttonRemoveAmpersand(props.label)} />
                </button>
            </ErrorCatcher>
        );
    }
};

Button.parseProps = (atts: ButtonWpAttrs) => {
    return {
        ...atts,
    };
};
